import React from 'react';
import { Text } from '@react-pdf/renderer';
import {
  coerceUnicodeGlyphsToAscii,
  normalizeForNativePdf,
  splitPdfTextFontSegments,
} from './normalizeForNativePdf';
import {
  isPdfUnicodeFontAvailable,
  PDF_FONT_REGULAR,
  PDF_UNICODE_FONT,
  pdfFontFamily,
} from './pdfFontSetup';

/**
 * Render PDF text with Times-Roman for WinAnsi text and Noto Sans for math symbols (≥, ≤, etc.).
 */
export function renderHybridPdfText(text, style = {}, keyPrefix = '') {
  const normalized = normalizeForNativePdf(String(text ?? ''));
  if (!isPdfUnicodeFontAvailable()) {
    return coerceUnicodeGlyphsToAscii(normalized);
  }
  const segments = splitPdfTextFontSegments(normalized);

  if (segments.length === 1 && segments[0].kind === 'regular') {
    return segments[0].text;
  }

  return segments.map((segment, index) => (
    <Text
      key={`${keyPrefix}${index}`}
      style={{
        ...style,
        fontFamily:
          segment.kind === 'unicode'
            ? PDF_UNICODE_FONT
            : style?.fontFamily || PDF_FONT_REGULAR,
      }}
    >
      {segment.text}
    </Text>
  ));
}

/** @deprecated use renderHybridPdfText */
export function splitTextForHybridFonts(text) {
  return splitPdfTextFontSegments(normalizeForNativePdf(String(text ?? ''))).map((segment) => ({
    text: segment.text,
    useSymbolFont: segment.kind === 'unicode',
  }));
}

export function needsSymbolFont(text) {
  return splitPdfTextFontSegments(normalizeForNativePdf(String(text ?? ''))).some(
    (segment) => segment.kind === 'unicode',
  );
}

/**
 * Flatten renderHybridPdfText output for nesting inside a parent <Text>.
 */
export function renderHybridPdfTextChildren(text, style = {}, keyPrefix = '') {
  const rendered = renderHybridPdfText(text, style, keyPrefix);
  if (typeof rendered === 'string' || rendered == null) {
    return rendered ?? '';
  }
  if (Array.isArray(rendered)) {
    return rendered;
  }
  return rendered;
}

export function pdfFontFamilyForStyle(style = {}) {
  if (style?.fontFamily === PDF_UNICODE_FONT) {
    return PDF_UNICODE_FONT;
  }
  return pdfFontFamily({
    bold: style?.fontFamily === 'Times-Bold' || style?.fontFamily?.includes?.('Bold'),
    italic: style?.fontFamily === 'Times-Italic',
  });
}
